import { Button, cn } from '@seen/ui'
import { IconArrowBackUp, IconCheck, IconX } from '@tabler/icons-react'
import type { JSX } from 'react'
import type { ValidatedToolFieldSuggestion } from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'

/* One field's decision controls, rendered at the SUGGESTED OPTION rather than in
 * the field's footer.
 *
 * Two things follow from that placement. The controls sit beside the thing they
 * decide, instead of under a six-option list where the clinician has to work out
 * which option the row refers to; and the decided state says WHICH option was
 * accepted or rejected by where it sits, which a footer marker could not. The
 * anchor is the same in every state, so nothing moves under the pointer at the
 * moment of decision.
 *
 * Pending carries no "suggested by AI" label: the highlight above it already
 * says so, and a staged Braden prints six of these. */
export function ValidatedToolFieldControls({
  linkId,
  suggestion,
}: {
  linkId: string
  suggestion: ValidatedToolFieldSuggestion
}): JSX.Element {
  if (suggestion.status === 'pending') {
    return (
      <div
        data-vt-field-controls={linkId}
        data-vt-field-decision="pending"
        className="mt-1 flex items-center gap-1"
      >
        <Button
          type="button"
          size="sm"
          variant="outline"
          color="blue"
          onClick={suggestion.onAccept}
        >
          <IconCheck className="size-3.5" />
          Accept
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={suggestion.onReject}
        >
          <IconX className="size-3.5" />
          Reject
        </Button>
      </div>
    )
  }

  const accepted = suggestion.status === 'accepted'
  return (
    <div
      data-vt-field-controls={linkId}
      data-vt-field-decision={suggestion.status}
      className="mt-1 flex items-center gap-2 text-sm"
    >
      {/* Colour and icon, not just the word, so accepted and rejected are told
          apart at a glance — they read as the same grey row otherwise. */}
      <span
        className={cn(
          'flex items-center gap-1',
          accepted
            ? 'text-green-700 dark:text-green-400'
            : 'text-red-700 dark:text-red-400'
        )}
      >
        {accepted ? (
          <IconCheck className="size-3.5" />
        ) : (
          <IconX className="size-3.5" />
        )}
        {accepted ? 'Accepted' : 'Rejected'}
      </span>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={suggestion.onUndo}
      >
        <IconArrowBackUp className="size-3.5" />
        Undo
      </Button>
    </div>
  )
}
